
import { Type } from "@earendil-works/pi-ai";

import { getDockBodyHost } from "./dockStore.js";
import { requestNavigation } from "./navigationStore.js";
import { coerceJsonArg } from "./coerceJsonArg.js";
import { navigableScopeDims, scopeActivityStatus, rangeToTimeRange } from "./analyticsClient.js";
import { toApiRange } from "./shell.js";
import { readUrlParam } from "./urlParams.js";
import { DEFAULT_RANGE } from "./dateRangePresets.js";

import { validateExploreScope, workspaceQueryForScope, describeScope, shouldNavigateDirectly, navStatusBlocks } from "./navIntent.js";

function isDocked() {
  return Boolean(getDockBodyHost());
}

function toolError(text) {
  return { isError: true, content: [{ type: "text", text }] };
}

// The existence checks fail closed on "unknown" (a probe error). Since
// that refuses a navigation the user asked for, absorb a single transient
// backend/IDB blip first: one retry after a short pause before giving up.
// "active"/"empty"/"exists"/"missing" are decisive — only "unknown" retries.
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
async function checkWithRetry(probe) {
  const first = await probe();
  if (first !== "unknown") return first;
  await sleep(300);
  return probe();
}

// Resolve the time range the landed Explore tab will inherit (?range=,
// default 1M) into a query timeRange — so the existence probe checks the
// SAME window the user will be looking at.
function currentTimeRange() {
  return rangeToTimeRange(toApiRange(readUrlParam("range") || DEFAULT_RANGE));
}

// Apply directly (docked + requested) or return an offer. `intent` is a
// validated navigation intent; `label` is the human description.
function dispatch(intent, label, requested) {
  if (shouldNavigateDirectly(requested, isDocked())) {
    requestNavigation(intent);
    return {
      content: [{ type: "text", text: `Opened ${label} in the main column.` }],
      details: { kind: "navApplied", intent, label },
    };
  }
  return {
    content: [{
      type: "text",
      text: `Offered to open ${label}. The user has a one-click button to open it.`,
    }],
    details: { kind: "navSuggestion", intent, label },
  };
}

export function createNavigationTools() {
  const openInWorkspace = {
    name: "open_in_workspace",
    description: [
      "Open selected activity in Investigations, or a single endpoint in Fleet. Use this when the user asks to inspect activity beyond the chat.",
      "",
      "scopePath is an array of steps, each { dim, ids }. `ids` MUST be real ids you got from an analytics_query grouped by that dim — never invent them. Steps nest (the 2nd step filters within the 1st).",
      `Navigable dims: ${navigableScopeDims().join(", ")}.`,
      "",
      "Set `requested: true` ONLY when the user explicitly asked to be taken there — then, if the chat is docked, the selection opens immediately. Otherwise the user gets a one-click navigation offer.",
    ].join("\n"),
    parameters: Type.Object({
      scopePath: Type.Array(
        Type.Object({
          dim: Type.String({ description: "dashboard dim key, e.g. endpoints / agents / sessions / clusters / models / tools / skills / mcp_servers" }),
          ids: Type.Array(Type.String(), { description: "value ids for this dim, taken from analytics_query results" }),
        }),
        { description: "ordered scope steps; later steps nest within earlier ones" },
      ),
      requested: Type.Optional(Type.Boolean({ description: "true only if the user explicitly asked to navigate" })),
    }),
    async execute(_id, args) {
      const scopePath = coerceJsonArg(args.scopePath);
      const v = validateExploreScope(scopePath);
      if (!v.ok) return toolError(v.error);
      try { workspaceQueryForScope(v.scopePath); } catch (error) { return toolError(error.message); }
      const label = describeScope(v.scopePath);
      // Existence check (fails CLOSED — the gate exists to prevent dead
      // navigations). Probe the SAME range the tab will land in, so we
      // catch both invented ids and scopes that are simply empty in the
      // user's current window.
      const timeRange = currentTimeRange();
      const status = await checkWithRetry(() => scopeActivityStatus(v.scopePath, timeRange));
      if (navStatusBlocks(status)) {
        return toolError(status === "empty"
          ? `No data found at scope "${label}" in the selected time range. Use ids from an analytics_query result or widen the date range.`
          : `Couldn't verify data at scope "${label}". Try again shortly.`);
      }
      const intent = { kind: "workspace", scopePath: v.scopePath, label };
      return dispatch(intent, label, Boolean(args.requested));
    },
  };

  return [openInWorkspace];
}
